import { NSIPS_CONSTANTS, NSIPS_ERRORS } from '@/constants/nsips'
import { NSIPSFormData } from '@/features/internal/types/nsips'

export const useNSIPSFormValidation = () => {
  const validatePatientCode = (code: string): string | null => {
    if (!code) return NSIPS_ERRORS.PATIENT_CODE_REQUIRED
    return null
  }

  const validatePatientKanaName = (name: string): string | null => {
    if (!name) return NSIPS_ERRORS.PATIENT_KANA_NAME_REQUIRED
    // カタカナ以外の文字が含まれている場合（全角カタカナと半角カタカナ、長音符を許可）
    const nonKanaChars = name.replace(/[ァ-ヶｦ-ﾟーｰ\s]/g, '')
    if (nonKanaChars.length > 0) {
      return NSIPS_ERRORS.PATIENT_KANA_NAME_FORMAT
    }
    return null
  }

  const validatePatientKanjiName = (name: string): string | null => {
    if (!name) return NSIPS_ERRORS.PATIENT_KANJI_NAME_REQUIRED
    return null
  }

  const validateGender = (gender: string): string | null => {
    if (!gender) return NSIPS_ERRORS.GENDER_REQUIRED
    return null
  }

  const validateBirthDate = (date: string): string | null => {
    if (!date) return NSIPS_ERRORS.BIRTH_DATE_REQUIRED
    if (!NSIPS_CONSTANTS.DATE_FORMAT_REGEX.test(date)) {
      return NSIPS_ERRORS.BIRTH_DATE_FORMAT
    }
    return null
  }

  const validateUsage = (usage: string): string | null => {
    if (!usage) return NSIPS_ERRORS.USAGE_REQUIRED
    return null
  }

  const validateDays = (days: string): string | null => {
    if (!days) return NSIPS_ERRORS.DAYS_REQUIRED
    return null
  }

  const validateDrugName = (name: string): string | null => {
    if (!name) return NSIPS_ERRORS.DRUG_NAME_REQUIRED
    return null
  }

  const validateQuantity = (quantity: string): string | null => {
    if (!quantity) return NSIPS_ERRORS.QUANTITY_REQUIRED
    return null
  }

  const validateUnit = (unit: string): string | null => {
    if (!unit) return NSIPS_ERRORS.UNIT_REQUIRED
    return null
  }

  const validateForm = (formData: NSIPSFormData): Record<string, string> => {
    const errors: Record<string, string> = {}

    // 患者情報のバリデーション
    const patientCodeError = validatePatientCode(formData.patientCode)
    if (patientCodeError) errors.patientCode = patientCodeError

    const kanaNameError = validatePatientKanaName(formData.patientKanaName)
    if (kanaNameError) errors.patientKanaName = kanaNameError

    const kanjiNameError = validatePatientKanjiName(formData.patientKanjiName)
    if (kanjiNameError) errors.patientKanjiName = kanjiNameError

    const genderError = validateGender(formData.gender)
    if (genderError) errors.gender = genderError

    const birthDateError = validateBirthDate(formData.birthDate)
    if (birthDateError) errors.birthDate = birthDateError

    // 処方データのバリデーション
    formData.prescriptions.forEach((prescription, rpIndex) => {
      const usageError = validateUsage(prescription.usage)
      if (usageError) errors[`rp_${rpIndex}_usage`] = usageError

      const daysError = validateDays(prescription.days)
      if (daysError) errors[`rp_${rpIndex}_days`] = daysError

      prescription.drugs.forEach((drug, drugIndex) => {
        const drugNameError = validateDrugName(drug.name)
        if (drugNameError) {
          errors[`rp_${rpIndex}_drug_${drugIndex}_name`] = drugNameError
        }

        const quantityError = validateQuantity(drug.quantity)
        if (quantityError) {
          errors[`rp_${rpIndex}_drug_${drugIndex}_quantity`] = quantityError
        }

        const unitError = validateUnit(drug.unit)
        if (unitError) {
          errors[`rp_${rpIndex}_drug_${drugIndex}_unit`] = unitError
        }
      })
    })

    return errors
  }

  // サニタイゼーション関数
  const sanitizeKanaName = (input: string): string => {
    // 全角カタカナと半角カタカナとスペースのみを許可
    return input.replace(NSIPS_CONSTANTS.KANA_REGEX, '')
  }

  const sanitizeBirthDate = (input: string): string => {
    // 数字のみを許可
    return input.replace(NSIPS_CONSTANTS.DIGIT_ONLY_REGEX, '')
  }

  const sanitizeNumeric = (input: string): string => {
    // 数字と小数点のみを許可
    return input.replace(/[^\d.]/g, '')
  }

  return {
    validatePatientCode,
    validatePatientKanaName,
    validatePatientKanjiName,
    validateGender,
    validateBirthDate,
    validateUsage,
    validateDays,
    validateDrugName,
    validateQuantity,
    validateUnit,
    validateForm,
    sanitizeKanaName,
    sanitizeBirthDate,
    sanitizeNumeric,
  }
}
